import * as React from "react";
import { ArrowRight, Ellipsis, TriangleAlert } from "lucide-react";
import { ConnectProto, JobEvent } from "@racoon/api";
import { IcAi, IcExcel, IcPlus } from "@racoon/icons";
import { Namespace, useDateFnsLocale, useT, useTypedT } from "@racoon/i18n";
import {
  buildYandexRouteUrl,
  countYandexRoutePoints,
  routeToYandexPoints,
  YANDEX_MAX_ROUTE_POINTS,
} from "@racoon/lib";
import {
  cn,
  CountBadge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  useModal,
  useToast,
} from "@racoon/ui";
import {
  useApproveRouteMutation,
  useCancelRouteMutation as useForceCancelRouteMutation,
  useForceCompleteRouteMutation,
} from "~/shared/api/routes/route.grpc";
import { useSolveSingleVehicleMutation } from "~/shared/api/routes/vrp-routing.grpc";
import { useRouteJobs } from "../../hooks/msw-route-list/useRouteJobs";
import { useAddJobsToRouteModal } from "../../hooks/useAddJobsToRouteModal";
import { exportRouteJobsToExcel } from "../../utils/routeJobsExport";
import { ForceCompleteModal } from "../ForceCompleteModal";
import { RouteTransferModalContent, type RouteTransferMode } from "./RouteTransferModal";
import { jobsForSelectedAction } from "./selected-jobs-action";
import { useSelectedJobsAction } from "./use-selected-jobs-action";

export const EMPTY_SELECTED_IDS = new Set<bigint>();

/**
 * What a trip's "⋯" menu does to it, with the job selection its removal and
 * transfer act on. One per cascade: render `modals` once beside the trips.
 */
export const useRouteActions = ({
  routes,
  orgId,
  selectedDate,
  onRefetch,
  openRouteJobs,
}: {
  routes: ConnectProto.Route[];
  orgId: bigint;
  selectedDate?: Date;
  onRefetch?: () => void;
  /** The route card; the caller knows whether a router is there for its links. */
  openRouteJobs: (route: ConnectProto.Route, options?: { onClose?: () => void }) => void;
}) => {
  const t = useTypedT(Namespace.Routes);
  const tAll = useT();
  const dateFnsLocale = useDateFnsLocale();
  const { openModal, closeModal } = useModal();
  const { openAddJobsToRoute } = useAddJobsToRouteModal();
  const { toast } = useToast();
  const jobs = useRouteJobs(routes, onRefetch);
  const { handleReorderJobs, clearAllSelection, dropFromSelection } = jobs;
  const selectedJobsAction = useSelectedJobsAction({
    routes,
    onSettled: React.useCallback(
      (routeId: bigint, settledKeys: ReadonlySet<string>) => {
        dropFromSelection(routeId, settledKeys);
        if (settledKeys.size > 0) onRefetch?.();
      },
      [dropFromSelection, onRefetch],
    ),
  });

  const [forceCompleteRouteMutation, { isLoading: isCompleting }] = useForceCompleteRouteMutation();
  const [approveRouteMutation, { isLoading: isApproving }] = useApproveRouteMutation();
  const [forceCancelRouteMutation, { isLoading: isCancelling }] = useForceCancelRouteMutation();
  const [solveSingleVehicle, { isLoading: isOptimizing }] = useSolveSingleVehicleMutation();

  const [isForceCompleteModalOpen, setIsForceCompleteModalOpen] = React.useState(false);
  const [selectedRouteForComplete, setSelectedRouteForComplete] = React.useState<bigint | null>(
    null,
  );
  const [isForceCancelModalOpen, setIsForceCancelModalOpen] = React.useState(false);
  const [selectedRouteForCancel, setSelectedRouteForCancel] = React.useState<bigint | null>(null);

  const handleOpenAddJobsModal = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      openAddJobsToRoute({
        route,
        currentJobIds: (route.jobs ?? []).map((j) => j.id),
        initialDate: selectedDate,
        onAdded: () => onRefetch?.(),
      });
    },
    [routes, openAddJobsToRoute, selectedDate, onRefetch],
  );

  const handleForceCompleteClick = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const unfinishedJobs = route.jobs.filter(
        (job) =>
          job.status !== ConnectProto.JobStatus.COMPLETED &&
          job.status !== ConnectProto.JobStatus.CANCELLED,
      );

      setSelectedRouteForComplete(routeId);

      if (unfinishedJobs.length > 0) {
        setIsForceCompleteModalOpen(true);
      } else {
        handleConfirmForceComplete(routeId, {});
      }
    },
    [routes],
  );

  const handleConfirmForceComplete = React.useCallback(
    async (routeId: bigint, resolutions: Record<any, any>) => {
      try {
        const jobsToComplete: bigint[] = [];
        const jobsToUnassign: bigint[] = [];

        Object.entries(resolutions).forEach(([jobId, event]) => {
          if (
            event === JobEvent.JOB_EVENT_FORCE_COMPLETE ||
            event === ConnectProto.JobEvent.FORCE_COMPLETE ||
            event === "FORCE_COMPLETE" ||
            String(event) === "FORCE_COMPLETE" ||
            event === "JOB_EVENT_FORCE_COMPLETE" ||
            String(event) === "JOB_EVENT_FORCE_COMPLETE"
          ) {
            jobsToComplete.push(BigInt(jobId));
          } else {
            jobsToUnassign.push(BigInt(jobId));
          }
        });

        await forceCompleteRouteMutation({
          id: routeId,
          jobsToComplete,
          jobsToUnassign,
          ownerOrganizationId: orgId,
        }).unwrap();

        toast({
          title: t("success"),
          description: t("routeCompleted"),
        });

        setIsForceCompleteModalOpen(false);
        setSelectedRouteForComplete(null);
        onRefetch?.();
      } catch {
        toast({
          title: t("error"),
          description: t("routeCompleteError"),
          variant: "destructive",
        });
      }
    },
    [forceCompleteRouteMutation, toast, orgId],
  );

  const handleForceCancelClick = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const unfinishedJobs = route.jobs.filter(
        (job) =>
          job.status !== ConnectProto.JobStatus.COMPLETED &&
          job.status !== ConnectProto.JobStatus.CANCELLED,
      );

      setSelectedRouteForCancel(routeId);

      if (unfinishedJobs.length > 0) {
        setIsForceCancelModalOpen(true);
      } else {
        handleConfirmForceCancel(routeId, {});
      }
    },
    [routes],
  );

  const handleApproveRouteClick = React.useCallback(
    async (routeId: bigint) => {
      try {
        await approveRouteMutation({ id: routeId, ownerOrganizationId: orgId }).unwrap();

        toast({
          title: t("success"),
          description: t("routeApproved"),
        });

        onRefetch?.();
      } catch (error) {
        console.log(error);
        toast({
          title: t("error"),
          description: t("routeApproveError"),
          variant: "destructive",
        });
      }
    },
    [approveRouteMutation, toast],
  );

  const handleConfirmForceCancel = React.useCallback(
    async (routeId: bigint, resolutions: Record<any, any>) => {
      try {
        const jobsToCancel: bigint[] = [];
        const jobsToUnassign: bigint[] = [];

        Object.entries(resolutions).forEach(([jobId, event]) => {
          if (
            event === ConnectProto.JobEvent.CANCEL ||
            event === "CANCEL" ||
            String(event) === "CANCEL" ||
            event === JobEvent.JOB_EVENT_CANCEL
          ) {
            jobsToCancel.push(BigInt(jobId));
          } else if (
            event === ConnectProto.JobEvent.DETACH_FROM_ROUTE ||
            event === "DETACH_FROM_ROUTE" ||
            String(event) === "DETACH_FROM_ROUTE" ||
            event === JobEvent.JOB_EVENT_DETACH_FROM_ROUTE
          ) {
            jobsToUnassign.push(BigInt(jobId));
          }
        });

        await forceCancelRouteMutation({
          id: routeId,
          jobsToCancel,
          jobsToUnassign,
          ownerOrganizationId: orgId,
        }).unwrap();

        toast({
          title: t("success"),
          description: t("routeCancelled"),
        });

        setIsForceCancelModalOpen(false);
        setSelectedRouteForCancel(null);
        onRefetch?.();
      } catch {
        toast({
          title: t("error"),
          description: t("routeCancelError"),
          variant: "destructive",
        });
      }
    },
    [forceCancelRouteMutation, toast, orgId],
  );

  const handleOpenRouteCard = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      openRouteJobs(route, { onClose: () => onRefetch?.() });
    },
    [routes, openRouteJobs, onRefetch],
  );

  const handleOpenInYandex = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const url = buildYandexRouteUrl(routeToYandexPoints(route));
      // Disabled unless a link can be built; guards a change between render
      // and click.
      if (!url) return;

      // noopener keeps the new tab from reaching back through window.opener.
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [routes],
  );

  const handleDownloadExcel = React.useCallback(
    (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      try {
        exportRouteJobsToExcel({
          route,
          t: tAll,
          dateFnsLocale,
          date: selectedDate,
        });
      } catch (error) {
        toast({
          title: t("error"),
          description: String((error as Error)?.message ?? error),
          variant: "destructive",
        });
      }
    },
    [routes, tAll, dateFnsLocale, selectedDate, toast, t],
  );

  /**
   * Ask the VRP solver for the cheapest visit order of this route's jobs and
   * save it back as the route's job order. The solver may split them across
   * several trips; a route is a single trip, so the trips are flattened in
   * execution order. Jobs it could not fit are kept at the end instead of being
   * dropped from the route.
   */
  const handleOptimizeRoute = React.useCallback(
    async (routeId: bigint) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const jobIds = route.jobs.map((job) => job.id);
      const driverOnShiftId = route.driverOnShift?.id;
      if (jobIds.length === 0 || driverOnShiftId == null) return;

      // The solver ends every trip at the disposal site, so the route needs one.
      if (!route.destinationLocationId) {
        toast({
          title: t("error"),
          description: t("optimizeSelectDepotFirst"),
          variant: "destructive",
        });
        return;
      }

      try {
        const response = await solveSingleVehicle({
          jobIds,
          disposalSiteId: route.destinationLocationId,
          driverOnShiftId,
        }).unwrap();

        if (response.responseStatus !== "ok") {
          toast({
            title: t("error"),
            description: response.errorMessage || t("optimizeFailed"),
            variant: "destructive",
          });
          return;
        }

        const orderedJobIds = response.trips.flatMap((trip) => trip.jobIds);
        const ordered = new Set(orderedJobIds.map(String));
        const leftoverJobIds = jobIds.filter((id) => !ordered.has(String(id)));
        const nextJobIds = [...orderedJobIds, ...leftoverJobIds];

        if (orderedJobIds.length === 0) {
          toast({
            title: t("error"),
            description: t("optimizeFailed"),
            variant: "destructive",
          });
          return;
        }

        await handleReorderJobs(route.id, nextJobIds);

        toast({
          title: t("success"),
          description:
            leftoverJobIds.length > 0
              ? t("optimizeUnassignedN", { count: leftoverJobIds.length })
              : t("routeOptimized"),
        });
      } catch (error) {
        console.error("Error optimizing route:", error);
        toast({
          title: t("error"),
          description: t("optimizeFailed"),
          variant: "destructive",
        });
      }
    },
    [routes, solveSingleVehicle, handleReorderJobs, toast, t],
  );

  const handleTransferClick = React.useCallback(
    (routeId: bigint, selectedJobIds: Set<bigint>, mode: RouteTransferMode = "jobs") => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const modalId = openModal({
        size: "2xl",
        width: { md: "min(1200px, 94vw)" },
        bodyClassName: "flex flex-col flex-1 !p-0 min-h-0",
        closeButtonTop: "top-4",
        body: (
          <RouteTransferModalContent
            mode={mode}
            sourceRoute={route}
            sourceSelectedJobIds={Array.from(selectedJobIds)}
            initialDate={selectedDate}
            onClose={() => closeModal(modalId)}
            onSuccess={() => {
              clearAllSelection();
              onRefetch?.();
            }}
          />
        ),
      });
    },
    [routes, openModal, closeModal, selectedDate, clearAllSelection, onRefetch],
  );

  const modals = (
    <>
      {isForceCompleteModalOpen && selectedRouteForComplete !== null && (
        <ForceCompleteModal
          open={isForceCompleteModalOpen}
          onOpenChange={setIsForceCompleteModalOpen}
          unfinishedJobs={
            (routes
              .find((r) => r.id === selectedRouteForComplete)
              ?.jobs.filter(
                (job) =>
                  job.status !== ConnectProto.JobStatus.COMPLETED &&
                  job.status !== ConnectProto.JobStatus.CANCELLED,
              ) || []) as any
          }
          onConfirm={(resolutions) =>
            selectedRouteForComplete !== null &&
            handleConfirmForceComplete(selectedRouteForComplete, resolutions)
          }
          isLoading={isCompleting}
        />
      )}
      {isForceCancelModalOpen && selectedRouteForCancel !== null && (
        <ForceCompleteModal
          open={isForceCancelModalOpen}
          onOpenChange={setIsForceCancelModalOpen}
          mode="cancel"
          unfinishedJobs={
            (routes
              .find((r) => r.id === selectedRouteForCancel)
              ?.jobs.filter(
                (job) =>
                  job.status !== ConnectProto.JobStatus.COMPLETED &&
                  job.status !== ConnectProto.JobStatus.CANCELLED,
              ) || []) as any
          }
          onConfirm={(resolutions) =>
            selectedRouteForCancel !== null &&
            handleConfirmForceCancel(selectedRouteForCancel, resolutions)
          }
          isLoading={isCancelling}
        />
      )}
      {selectedJobsAction.undoBar}
    </>
  );

  return {
    ...jobs,
    handleOpenAddJobsModal,
    handleForceCompleteClick,
    handleForceCancelClick,
    handleApproveRouteClick,
    handleOpenRouteCard,
    handleOpenInYandex,
    handleDownloadExcel,
    handleOptimizeRoute,
    handleTransferClick,
    scheduleSelectedJobsAction: selectedJobsAction.schedule,
    isSelectedJobsActionBusy: selectedJobsAction.isBusy,
    isCompleting,
    isApproving,
    isCancelling,
    isOptimizing,
    modals,
  };
};

/**
 * A row of the "⋯" menu; rows are split by straight full-width hairlines, as in
 * the design. `cn` only joins classes, so the item's own styles are beaten with
 * `!`: its `rounded-sm` would curve the hairline ends, and its disabled
 * `opacity-50` would fade the hairline above a disabled row. A disabled row
 * greys its text (Figma Text/disabled) and fades only its icon instead.
 */
const MenuRow = ({ className, ...props }: React.ComponentProps<typeof DropdownMenuItem>) => (
  <DropdownMenuItem
    className={cn(
      "min-h-11 gap-2 !rounded-none px-3 py-2.5",
      "data-[disabled]:!opacity-100 data-[disabled]:text-slate-400 data-[disabled]:[&_svg]:opacity-50",
      className,
    )}
    {...props}
  />
);

/** How many ticked jobs a row acts on; nothing is shown when it acts on none. */
const RowCount = ({ count }: { count: number }) =>
  count > 0 ? <CountBadge count={count} className="ml-auto" /> : null;

export const RouteActionsMenu = ({
  route,
  selectedJobIds,
  readOnly = false,
  actions,
}: {
  route: ConnectProto.Route;
  selectedJobIds: Set<bigint>;
  /** The TTN tab: the route itself is not edited from here. */
  readOnly?: boolean;
  actions: ReturnType<typeof useRouteActions>;
}) => {
  const t = useTypedT(Namespace.Routes);
  const {
    isUpdatingRoute,
    handleRemoveSelectedJobs,
    handleOpenAddJobsModal,
    handleForceCompleteClick,
    handleForceCancelClick,
    handleApproveRouteClick,
    handleOpenRouteCard,
    handleOpenInYandex,
    handleDownloadExcel,
    handleOptimizeRoute,
    handleTransferClick,
    scheduleSelectedJobsAction,
    isSelectedJobsActionBusy,
    isCompleting,
    isApproving,
    isCancelling,
    isOptimizing,
  } = actions;

  // Warned in the label, not a toast: the new tab takes focus at once.
  const yandexPointCount = countYandexRoutePoints(routeToYandexPoints(route));
  const yandexTruncated = yandexPointCount > YANDEX_MAX_ROUTE_POINTS;

  // Settled jobs and closed trips are skipped, so the badges count what will change.
  const actionableCount = jobsForSelectedAction(route, selectedJobIds).length;

  // Status validation for force actions
  const canForceComplete =
    route.status === ConnectProto.RouteStatus.IN_PROGRESS ||
    route.status === ConnectProto.RouteStatus.RETURNING;
  const canForceCancel =
    route.status === ConnectProto.RouteStatus.CREATED ||
    route.status === ConnectProto.RouteStatus.IN_PROGRESS;
  const canAddJobs =
    route.status !== ConnectProto.RouteStatus.COMPLETED &&
    route.status !== ConnectProto.RouteStatus.CANCELLED &&
    route.status !== ConnectProto.RouteStatus.FORCE_COMPLETED;
  const disableApprove = route.status !== ConnectProto.RouteStatus.NOT_APPROVED;
  // Handing the whole route to another shift only makes sense while the
  // driver has not started it.
  // Re-ordering a route the driver is already executing would move points
  // the truck has passed, so optimization is offered before it starts.
  const canOptimize =
    route.driverOnShift != null &&
    (route.status === ConnectProto.RouteStatus.CREATED ||
      route.status === ConnectProto.RouteStatus.NOT_APPROVED);
  const canTransferRoute =
    route.status === ConnectProto.RouteStatus.CREATED ||
    route.status === ConnectProto.RouteStatus.NOT_APPROVED;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          size="md"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Ellipsis className="h-4 w-4 text-black" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        // `!p-0`: the content's own `p-1` would inset every hairline.
        className="w-80 divide-y divide-slate-200 !p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {!readOnly && (
          <>
            <MenuRow
              onClick={() => handleTransferClick(route.id, selectedJobIds)}
              disabled={selectedJobIds.size === 0}
            >
              {t("transferJobs")}
              <RowCount count={selectedJobIds.size} />
            </MenuRow>
            <MenuRow
              data-testid="route-menu-cancel-selected-jobs"
              onClick={() => scheduleSelectedJobsAction("cancel", route, selectedJobIds)}
              disabled={isSelectedJobsActionBusy || actionableCount === 0}
            >
              {t("cancelSelectedJobs")}
              <RowCount count={actionableCount} />
            </MenuRow>
            <MenuRow
              data-testid="route-menu-complete-selected-jobs"
              onClick={() => scheduleSelectedJobsAction("complete", route, selectedJobIds)}
              disabled={isSelectedJobsActionBusy || actionableCount === 0}
            >
              {t("completeSelectedJobs")}
              <RowCount count={actionableCount} />
            </MenuRow>
            <MenuRow
              onClick={() => handleRemoveSelectedJobs(route, selectedJobIds)}
              disabled={isUpdatingRoute || actionableCount === 0}
            >
              {t("removeSelectedJobs")}
              <RowCount count={actionableCount} />
            </MenuRow>
            <MenuRow
              onClick={() => handleOptimizeRoute(route.id)}
              disabled={isOptimizing || isUpdatingRoute || !canOptimize || route.jobs.length === 0}
            >
              <IcAi className="h-5 w-5 shrink-0 text-brand-purple" />
              {isOptimizing ? t("optimizing") : t("optimizeRoute")}
            </MenuRow>
            <MenuRow
              onClick={() => handleOpenAddJobsModal(route.id)}
              disabled={isUpdatingRoute || !canAddJobs}
            >
              <IcPlus className="h-5 w-5 shrink-0 text-brand-blue" />
              {t("addJob")}
            </MenuRow>
            <MenuRow
              onClick={() => handleTransferClick(route.id, EMPTY_SELECTED_IDS, "route")}
              disabled={!canTransferRoute}
            >
              {/* Figma Icons/default #151A24, no token yet — as IconButton writes it. */}
              <ArrowRight className="h-5 w-5 shrink-0 text-[#151a24]" />
              {t("transferRoute")}
            </MenuRow>
            <MenuRow
              onClick={() => handleForceCompleteClick(route.id)}
              disabled={isCompleting || !canForceComplete}
            >
              {t("forceCompleteRoute")}
            </MenuRow>
            <MenuRow
              onClick={() => handleApproveRouteClick(route.id)}
              disabled={
                isApproving ||
                disableApprove ||
                route.driverOnShift?.status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED
              }
            >
              {t("approveRoute")}
            </MenuRow>
            <MenuRow
              onClick={() => handleForceCancelClick(route.id)}
              disabled={isCancelling || !canForceCancel}
            >
              {t("cancelRoute")}
            </MenuRow>
            {/* Not in the design's list; kept below it until it says where they go. */}
            <MenuRow onClick={() => handleOpenRouteCard(route.id)}>{t("openRouteCard")}</MenuRow>
          </>
        )}
        <MenuRow onClick={() => handleOpenInYandex(route.id)} disabled={yandexPointCount < 2}>
          {yandexTruncated && <TriangleAlert className="h-4 w-4 shrink-0 text-text-warning" />}
          {yandexTruncated
            ? t("openInYandexTruncated", {
                shown: YANDEX_MAX_ROUTE_POINTS,
                total: yandexPointCount,
              })
            : t("openInYandex")}
        </MenuRow>
        <MenuRow onClick={() => handleDownloadExcel(route.id)} disabled={route.jobs.length === 0}>
          <IcExcel className="h-4 w-4 text-status-success" />
          {`${t("download")} Excel`}
        </MenuRow>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
